import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import API from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Calendar, Users, IndianRupee, Clock, ArrowRight,
  UserPlus, ClipboardList, TrendingUp, AlertCircle, Heart, Activity, AlertTriangle,
  Sparkles, Zap, CheckCircle, Loader2, Video, Stethoscope, Receipt
} from 'lucide-react';
import axios from 'axios';
import BannerCarousel from '../components/BannerCarousel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Force Jitsi meeting links to English regardless of browser language
const joinMeetingUrl = (link) => {
  if (!link) return link;
  try {
    const u = new URL(link, window.location.origin);
    if (u.hostname.includes('jit.si')) u.searchParams.set('lang', 'en');
    return u.toString();
  } catch { return link; }
};

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [vitalsStats, setVitalsStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableOffers, setAvailableOffers] = useState([]);
  const [showOfferPopup, setShowOfferPopup] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [claimingOffer, setClaimingOffer] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(true);
  const [banners, setBanners] = useState([]);
  const [vendorBannersFeed, setVendorBannersFeed] = useState([]);
  const [portalLock, setPortalLock] = useState(null);
  const [dashAnalytics, setDashAnalytics] = useState(null);

  useEffect(() => {
    const todayStr = new Date().toISOString().split('T')[0];
    const fetchAll = () => Promise.all([
      API.get('/appointments/today-stats'),
      API.get('/appointments/queue'),
      API.get('/face-vitals/doctor-stats').catch(() => ({ data: null })),
      API.get('/patient-consultation/doctor/consultations?status=confirmed&limit=50').catch(() => ({ data: { consultations: [] } })),
      API.get('/analytics/dashboard').catch(() => ({ data: null }))
    ]).then(([statsRes, queueRes, vitalsRes, consultRes, analyticsRes]) => {
      setStats(statsRes.data);
      const videoToday = (consultRes.data.consultations || []).filter(c => c.preferred_date === todayStr).map(c => ({
        id: c.id,
        kind: 'video',
        patient_name: c.patient_name,
        time_slot: c.preferred_time,
        status: c.status === 'confirmed' ? 'waiting' : c.status,
        token_number: null,
        meeting_link: c.meeting_link,
      }));
      const physical = (queueRes.data || []).map(a => ({ ...a, kind: 'appointment' }));
      setQueue([...physical, ...videoToday]);
      setVitalsStats(vitalsRes.data);
      setDashAnalytics(analyticsRes.data);
    }).catch(console.error);

    fetchAll().finally(() => setLoading(false));

    // Fetch available offers
    const token = localStorage.getItem('clinic_token');
    if (token) {
      // Check portal lock status
      axios.get(`${API_URL}/api/credit/portal-lock-status`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        if (res.data.locked) setPortalLock(res.data);
      }).catch(() => {});

      axios.get(`${API_URL}/api/subscriptions/offers/available`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setAvailableOffers(res.data.offers || []);
        setHasActiveSubscription(res.data.has_active_subscription || false);
      }).catch(() => {});

      // Fetch banner ads
      axios.get(`${API_URL}/api/banners/doctor-feed`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setBanners(res.data.admin_banners || []);
        setVendorBannersFeed(res.data.vendor_banners || []);
      }).catch(() => {});
    }

    const interval = setInterval(() => { if (!document.hidden) fetchAll(); }, 15000);
    const onVis = () => { if (!document.hidden) fetchAll(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  const claimOffer = async (offer) => {
    setClaimingOffer(true);
    try {
      const token = localStorage.getItem('clinic_token');
      await axios.post(`${API_URL}/api/subscriptions/offers/${offer.id}/claim`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowOfferPopup(false);
      setAvailableOffers([]);
      setHasActiveSubscription(true);
      window.location.reload();
    } catch (err) {
      const { toast } = await import('sonner');
      toast.error(err.response?.data?.detail || 'Failed to claim offer');
    } finally { setClaimingOffer(false); }
  };

  const formatDuration = (days) => {
    if (days === 7) return '1 Week';
    if (days === 14) return '2 Weeks';
    if (days === 30) return '1 Month';
    if (days === 60) return '2 Months';
    if (days === 90) return '3 Months';
    if (days === 180) return '6 Months';
    if (days === 365) return '1 Year';
    return `${days} Days`;
  };

  const handleStatusChange = async (id, status) => {
    try {
      await API.put(`/appointments/${id}`, { status });
      setQueue(prev => prev.map(q => q.id === id ? { ...q, status } : q));
    } catch (err) {
      console.error(err);
    }
  };

  const handleJoinVideoConsult = (link) => {
    window.open(joinMeetingUrl(link), '_blank');
  };

  const statusColors = {
    waiting: 'bg-amber-100 text-amber-800',
    'in-progress': 'bg-blue-100 text-blue-800',
    completed: 'bg-emerald-100 text-emerald-800',
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-teal-700 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Portal Lock Warning */}
      {portalLock && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6 text-center" data-testid="portal-lock-banner">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-red-700 mb-2">Portal Access Restricted</h2>
          <p className="text-red-600 mb-2">
            Your portal is currently in <strong>read-only mode</strong> due to outstanding credit payment of{' '}
            <strong>₹{portalLock.outstanding_amount?.toLocaleString()}</strong>.
          </p>
          <p className="text-sm text-red-500 mb-4">
            You cannot create new invoices, orders, or appointments until the outstanding amount is cleared.
            Please recharge your wallet to resume full access.
          </p>
          <Link to="/doctor/wallet">
            <Button className="bg-red-600 hover:bg-red-700 text-white">
              <IndianRupee className="w-4 h-4 mr-1" /> Recharge Wallet Now
            </Button>
          </Link>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white" style={{fontFamily:'Manrope'}}>
            Good {new Date().getHours() < 12 ? 'Morning' : new Date().getHours() < 17 ? 'Afternoon' : 'Evening'}, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/appointments">
            <Button className="bg-teal-700 hover:bg-teal-800 text-white h-11" data-testid="dashboard-new-appointment-btn">
              <Calendar className="w-4 h-4 mr-2" /> New Appointment
            </Button>
          </Link>
        </div>
      </div>

      {/* Free Trial / Offer Banner - only for unsubscribed doctors */}
      {!hasActiveSubscription && availableOffers.length > 0 && (
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 p-[2px]" data-testid="free-trial-banner">
          <div className="rounded-[10px] bg-gradient-to-r from-orange-50 to-amber-50 p-4 sm:p-5 flex flex-col sm:flex-row items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <h3 className="font-bold text-slate-900 text-lg">{availableOffers[0].title}</h3>
              <p className="text-sm text-slate-600 mt-0.5">
                {availableOffers[0].offer_type === 'free_trial'
                  ? `Get all premium features free for ${formatDuration(availableOffers[0].duration_days)}! No payment required.`
                  : availableOffers[0].description || `Special offer for ${formatDuration(availableOffers[0].duration_days)}`}
              </p>
            </div>
            <Button 
              onClick={() => { setSelectedOffer(availableOffers[0]); setShowOfferPopup(true); }}
              className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-bold px-6 h-11 shadow-lg shadow-orange-200"
              data-testid="start-free-trial-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              {availableOffers[0].offer_type === 'free_trial' 
                ? `Start Free Trial` 
                : 'Claim Offer'}
            </Button>
          </div>
        </div>
      )}

      {/* Banner Ads Carousel - Admin (shown to all doctors) */}
      {banners.length > 0 && (
        <BannerCarousel banners={banners} autoPlayInterval={4000} />
      )}

      {/* Vendor Banner Ads (from linked vendors only) */}
      {vendorBannersFeed.length > 0 && (
        <div>
          <p className="text-xs text-slate-400 mb-1.5 flex items-center gap-1">
            From your vendors
          </p>
          <BannerCarousel banners={vendorBannersFeed} autoPlayInterval={5000} />
        </div>
      )}

      {/* Urgent Alerts Panel */}
      {vitalsStats?.attention_cases?.length > 0 && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-900/10" data-testid="urgent-alerts-panel">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2.5">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              <p className="font-bold text-sm text-amber-800" style={{ fontFamily: 'Manrope' }}>Urgent Alerts</p>
              <Badge className="bg-amber-500 text-white text-[10px]">{vitalsStats.attention_cases.length} Need Attention</Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {vitalsStats.attention_cases.slice(0, 6).map((c, i) => (
                <div key={`alert-${i}`} className="flex items-center justify-between bg-white dark:bg-slate-900 rounded-lg px-3 py-2 border border-amber-100" data-testid={`urgent-alert-${i}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Activity className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    <span className="text-sm font-medium text-slate-700 truncate">{c.patient_name || c.patient_phone || 'Walk-in'}</span>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {c.attention_reasons?.slice(0, 1).map((r, j) => (
                      <Badge key={j} variant="outline" className="text-[9px] border-amber-200 text-amber-600">{r}</Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Micro-Analytical Ribbon: Today's Revenue, Completed Invoices, Total Patients */}
      <div className="grid grid-cols-3 gap-4 stagger-children" data-testid="dashboard-stats">
        <Card className="card-hover border-slate-200 dark:border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-teal-50 dark:bg-teal-900/20 flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-teal-600" />
              </div>
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="dashboard-stat-revenue">
              {'\u20B9'}{(dashAnalytics?.daily_revenue ?? stats?.revenue ?? 0).toLocaleString('en-IN')}
            </p>
            <p className="text-xs text-slate-500 mt-1">Today's Revenue</p>
          </CardContent>
        </Card>

        <Card className="card-hover border-slate-200 dark:border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                <Receipt className="w-5 h-5 text-emerald-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="dashboard-stat-invoices">
              {dashAnalytics?.today_invoices_count ?? 0}
            </p>
            <p className="text-xs text-slate-500 mt-1">Completed Invoices</p>
          </CardContent>
        </Card>

        <Card className="card-hover border-slate-200 dark:border-slate-800">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white" data-testid="dashboard-stat-total-patients">
              {dashAnalytics?.total_patients ?? 0}
            </p>
            <p className="text-xs text-slate-500 mt-1">Total Patients</p>
          </CardContent>
        </Card>
      </div>

      {/* Face Vitals Widget */}
      {vitalsStats && (vitalsStats.total_scans > 0 || vitalsStats.today_scans > 0) && (
        <Card className="border-slate-200 dark:border-slate-800 bg-gradient-to-r from-teal-50/50 to-white dark:from-teal-900/10" data-testid="dashboard-face-vitals-widget">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                  <Heart className="w-4 h-4 text-teal-600" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-slate-800" style={{fontFamily:'Manrope'}}>Face Vitals Scans</p>
                  <p className="text-xs text-slate-500">{vitalsStats.today_scans} today &middot; {vitalsStats.total_scans} total</p>
                </div>
              </div>
            </div>
            {(!vitalsStats.attention_cases || vitalsStats.attention_cases.length === 0) && vitalsStats.recent_scans?.length > 0 && (
              <p className="text-xs text-emerald-600 flex items-center gap-1"><Activity className="w-3 h-3" /> All recent scans within normal range</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Queue & Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Unified Queue: physical appointments + video consultations */}
        <Card className="lg:col-span-2 border-slate-200 dark:border-slate-800">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>Today's Patient Queue</CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">{stats?.total || 0} Total</Badge>
                <Badge className="bg-amber-100 text-amber-800 text-xs">{stats?.waiting || 0} Waiting</Badge>
                <Link to="/appointments" className="text-sm text-teal-700 hover:text-teal-800 font-medium flex items-center gap-1">
                  View All <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="dashboard-queue">
            {queue.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">No patients in queue today</p>
                <Link to="/appointments">
                  <Button variant="outline" className="mt-3" size="sm" data-testid="dashboard-add-appointment-link">
                    <UserPlus className="w-4 h-4 mr-2" /> Add Appointment
                  </Button>
                </Link>
              </div>
            ) : (
              queue.map((appt, i) => (
                <div
                  key={appt.id}
                  className={`flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3.5 rounded-lg border-l-4 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 transition-all duration-200 hover:shadow-sm ${appt.kind === 'video' ? 'border-l-purple-400' : `appt-border-${appt.status}`}`}
                  data-testid={`queue-item-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center ${appt.kind === 'video' ? 'bg-purple-100' : 'bg-slate-100 dark:bg-slate-800'}`}>
                      {appt.kind === 'video' ? <Video className="w-4 h-4 text-purple-600" /> : <span className="text-sm font-bold text-slate-600">#{appt.token_number}</span>}
                    </div>
                    <div>
                      <p className="font-medium text-sm text-slate-900 dark:text-white">{appt.patient_name}</p>
                      <p className="text-xs text-slate-400">{appt.time_slot || appt.type}{appt.kind === 'video' ? ' · Video Consult' : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[10px] ${statusColors[appt.status] || 'bg-slate-100 text-slate-600'}`}>
                      {appt.status}
                    </Badge>
                    {appt.kind === 'video' ? (
                      <Button
                        size="sm"
                        className="h-8 bg-purple-600 hover:bg-purple-700 text-white text-xs"
                        onClick={() => handleJoinVideoConsult(appt.meeting_link)}
                        disabled={!appt.meeting_link}
                        data-testid={`queue-consult-${i}`}
                      >
                        <Video className="w-3.5 h-3.5 mr-1" /> Click to Consult
                      </Button>
                    ) : (
                      <>
                        {appt.status === 'waiting' && (
                          <Button
                            size="sm"
                            className="h-8 bg-blue-600 hover:bg-blue-700 text-white text-xs"
                            onClick={() => handleStatusChange(appt.id, 'in-progress')}
                            data-testid={`queue-start-${i}`}
                          >
                            Start
                          </Button>
                        )}
                        {appt.status === 'in-progress' && (
                          <Button
                            size="sm"
                            className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                            onClick={() => handleStatusChange(appt.id, 'completed')}
                            data-testid={`queue-complete-${i}`}
                          >
                            Done
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-slate-200 dark:border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {[
              { label: 'New Patient', icon: UserPlus, path: '/patients', color: 'text-teal-600 bg-teal-50' },
              { label: 'Walk-in', icon: Users, path: '/appointments', color: 'text-blue-600 bg-blue-50' },
              { label: 'Consultation Hub', icon: Stethoscope, path: '/clinical', color: 'text-purple-600 bg-purple-50' },
              { label: 'Billing', icon: IndianRupee, path: '/billing', color: 'text-amber-600 bg-amber-50' },
            ].map(action => (
              <Link key={action.path + action.label} to={action.path}>
                <div className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors cursor-pointer" data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${action.color}`}>
                    <action.icon className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{action.label}</span>
                  <ArrowRight className="w-4 h-4 text-slate-300 ml-auto" />
                </div>
              </Link>
            ))}
          </CardContent>

          {/* AI Warning Notice */}
          <div className="mx-6 mb-6 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/20">
            <div className="flex gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-medium text-amber-800 dark:text-amber-400">AI-Assisted System</p>
                <p className="text-[11px] text-amber-600 dark:text-amber-500 mt-0.5">All AI suggestions require doctor approval before saving.</p>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Offer Claim Popup */}
      <Dialog open={showOfferPopup} onOpenChange={setShowOfferPopup}>
        <DialogContent className="max-w-md" data-testid="offer-claim-dialog">
          <DialogHeader>
            <DialogTitle className="text-center text-xl" style={{ fontFamily: 'Manrope' }}>
              {selectedOffer?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedOffer && (
            <div className="text-center space-y-4 py-2">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <p className="text-slate-600 text-sm">
                {selectedOffer.description || (selectedOffer.offer_type === 'free_trial'
                  ? 'Unlock all premium features at absolutely no cost.'
                  : `Special discount on your subscription.`)}
              </p>
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <Badge className="bg-orange-600 text-sm px-3 py-1">
                    {selectedOffer.offer_type === 'free_trial' ? '₹0 FREE' 
                      : selectedOffer.offer_type === 'percentage' ? `${selectedOffer.discount_value}% OFF`
                      : `₹${selectedOffer.discount_value} OFF`}
                  </Badge>
                </div>
                <p className="text-lg font-bold text-slate-800">
                  {formatDuration(selectedOffer.duration_days)} Access
                </p>
                <div className="text-xs text-slate-500 space-y-1">
                  <p className="flex items-center justify-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> All Premium Features Included</p>
                  <p className="flex items-center justify-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> AI Scribe, Analytics, Marketing & More</p>
                  <p className="flex items-center justify-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> No Payment Required</p>
                  <p className="flex items-center justify-center gap-1"><AlertCircle className="w-3 h-3 text-amber-500" /> Expires after {formatDuration(selectedOffer.duration_days)}</p>
                </div>
              </div>
              <Button
                onClick={() => claimOffer(selectedOffer)}
                disabled={claimingOffer}
                className="w-full h-12 text-base font-bold bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 shadow-lg shadow-orange-200"
                data-testid="claim-offer-btn"
              >
                {claimingOffer ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Zap className="w-5 h-5 mr-2" />
                )}
                {selectedOffer.offer_type === 'free_trial'
                  ? `Start Free Trial for ${formatDuration(selectedOffer.duration_days)}`
                  : `Claim ${formatDuration(selectedOffer.duration_days)} Offer`}
              </Button>
              <p className="text-[10px] text-slate-400">
                Features will expire automatically after {formatDuration(selectedOffer.duration_days)}. You can upgrade to a paid plan anytime.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
